

import React, {
    useEffect,
    useRef,
    useState
} from "react";

import EvaVideo from "./EvaVideo";

import EvaLiveConversationDrawer
    from "./EvaLiveConversationDrawer";

import EvaResourceLibrary
    from "./EvaResourceLibrary";

import {
    JWT
} from "../../../../constants/local-storage";


//========================================================
// Eva Live Expert
//
// OmniScan provides the intelligence.
// Browser speech recognition provides listening.
// Existing EvaVideo provides Simli video and speech.
//
// EvaResourceLibrary provides:
// - Resource count indicator
// - Left-hand resource drawer
// - Document and web resource handling
//========================================================

export default function EvaLiveExpertSimli()
{

    //----------------------------------------------------
    // Settings
    //----------------------------------------------------

    const SILENCE_DELAY = 1500;
    const RESTART_DELAY = 750;

    // Conservative speech estimate because the existing
    // EvaVideo.speak() resolves when PCM is sent to Simli,
    // not when playback actually finishes.

    const SPEECH_WORDS_PER_MINUTE = 125;
    const SPEECH_EXTRA_DELAY = 1500;


    //----------------------------------------------------
    // References
    //----------------------------------------------------

    const evaVideoRef = useRef(null);
    const recognitionRef = useRef(null);

    const activeRef = useRef(false);
    const connectedRef = useRef(false);
    const listeningRef = useRef(false);
    const thinkingRef = useRef(false);
    const speakingRef = useRef(false);

    const mountedRef = useRef(false);
    const sessionRef = useRef(0);

    const transcriptRef = useRef("");

    const silenceTimerRef = useRef(null);
    const restartTimerRef = useRef(null);
    const speechTimerRef = useRef(null);

    const finishSpeechRef = useRef(null);


    //----------------------------------------------------
    // State
    //----------------------------------------------------

    const [started, setStarted] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [connected, setConnected] = useState(false);

    const [listening, setListening] = useState(false);
    const [thinking, setThinking] = useState(false);
    const [speaking, setSpeaking] = useState(false);

    const [error, setError] = useState(null);
    const [messages, setMessages] = useState([]);

    //----------------------------------------------------
    // Eva Resource Library Response
    //----------------------------------------------------

    const [evaResponse, setEvaResponse] = useState(null);


    //====================================================
    // Ask OmniScan Eva
    //====================================================

    async function askEva(message)
    {

        const response = await fetch(
            "/resolutionsoftwareplatform/eva/chat",
            {
                method: "POST",

                headers:
                {
                    "Authorization":
                        localStorage.getItem(JWT),

                    "Accept":
                        "application/json",

                    "Content-Type":
                        "application/json"
                },

                body: JSON.stringify({
                    message: message
                })
            }
        );


        if (!response.ok)
        {

            throw new Error(
                "Unable to ask Eva: " +
                response.status
            );

        }


        return await response.json();

    }


    //====================================================
    // Clear Timers
    //====================================================

    const clearTimers = () =>
    {

        clearTimeout(silenceTimerRef.current);
        clearTimeout(restartTimerRef.current);
        clearTimeout(speechTimerRef.current);

        silenceTimerRef.current = null;
        restartTimerRef.current = null;
        speechTimerRef.current = null;

    };


    //====================================================
    // Stop Listening
    //====================================================

    const stopListening = () =>
    {

        clearTimeout(silenceTimerRef.current);

        silenceTimerRef.current = null;

        listeningRef.current = false;
        setListening(false);


        if (recognitionRef.current)
        {

            try
            {

                recognitionRef.current.stop();

            }
            catch (exception)
            {

                // Recognition already stopped.

            }

        }

    };


    //====================================================
    // Start Listening
    //====================================================

    const startListening = () =>
    {

        if (
            !activeRef.current ||
            !connectedRef.current ||
            thinkingRef.current ||
            speakingRef.current ||
            listeningRef.current ||
            !recognitionRef.current
        )
        {

            return;

        }


        clearTimeout(restartTimerRef.current);

        transcriptRef.current = "";


        try
        {

            listeningRef.current = true;
            setListening(true);

            recognitionRef.current.start();

        }
        catch (exception)
        {

            listeningRef.current = false;
            setListening(false);

            console.warn(
                "Unable to start microphone:",
                exception
            );

        }

    };


    //====================================================
    // Resume Listening
    //====================================================

    const resumeListening = () =>
    {

        clearTimeout(restartTimerRef.current);

        restartTimerRef.current = setTimeout(
            () =>
            {

                startListening();

            },
            RESTART_DELAY
        );

    };


    //====================================================
    // Speech Duration Estimate
    //====================================================

    const estimateSpeechDuration = text =>
    {

        const wordCount =
            String(text || "")
                .trim()
                .split(/\s+/)
                .filter(Boolean)
                .length;


        const duration =
            (
                wordCount /
                SPEECH_WORDS_PER_MINUTE
            ) * 60000;


        return Math.max(
            2500,
            duration + SPEECH_EXTRA_DELAY
        );

    };


    //====================================================
    // Speak Through Existing EvaVideo
    //====================================================

    const speakResponse = async (text, session) =>
    {

        if (
            !text ||
            !activeRef.current ||
            session !== sessionRef.current
        )
        {

            return;

        }


        stopListening();

        speakingRef.current = true;
        setSpeaking(true);


        const speechText =
            text
                .replace(/\*\*/g, "")
                .replace(/\*/g, "")
                .replace(/#/g, "")
                .replace(/`/g, "");


        try
        {

            if (!evaVideoRef.current?.speak)
            {

                throw new Error(
                    "Fiona video is not ready."
                );

            }


            //------------------------------------------------
            // Existing Working Simli Implementation
            //------------------------------------------------

            await evaVideoRef.current.speak(
                speechText
            );


            if (
                !activeRef.current ||
                session !== sessionRef.current
            )
            {

                return;

            }


            //------------------------------------------------
            // Allow Time For Simli Playback
            //------------------------------------------------

            const duration =
                estimateSpeechDuration(speechText);


            speechTimerRef.current = setTimeout(
                () =>
                {

                    if (
                        !activeRef.current ||
                        session !== sessionRef.current
                    )
                    {

                        return;

                    }


                    speakingRef.current = false;
                    setSpeaking(false);

                    resumeListening();

                },
                duration
            );

        }
        catch (exception)
        {

            console.error(
                "Unable to make Fiona speak:",
                exception
            );


            if (session === sessionRef.current)
            {

                speakingRef.current = false;
                setSpeaking(false);

                resumeListening();

            }

        }

    };


    //====================================================
    // Send Message To OmniScan
    //====================================================

    const sendFionaMessage = async text =>
    {

        const userMessage =
            String(text || "").trim();


        if (!userMessage)
        {

            return;

        }


        if (
            !activeRef.current ||
            !connectedRef.current
        )
        {

            throw new Error(
                "Fiona is not connected."
            );

        }


        if (
            thinkingRef.current ||
            speakingRef.current
        )
        {

            throw new Error(
                "Fiona is still responding."
            );

        }


        const session = sessionRef.current;


        //------------------------------------------------
        // Stop Listening During Request
        //------------------------------------------------

        stopListening();

        thinkingRef.current = true;
        setThinking(true);


        //------------------------------------------------
        // Display User Message
        //------------------------------------------------

        setMessages(previous => [
            ...previous,
            {
                id: Date.now() + Math.random(),
                role: "user",
                text: userMessage
            }
        ]);


        try
        {

            //------------------------------------------------
            // Existing OmniScan Chat Service
            //------------------------------------------------

            const response =
                await askEva(userMessage);


            //------------------------------------------------
            // Validate Active Session
            //------------------------------------------------

            if (
                !mountedRef.current ||
                !activeRef.current ||
                session !== sessionRef.current
            )
            {

                return;

            }


            //------------------------------------------------
            // Store Complete Response For Resource Library
            //------------------------------------------------

            setEvaResponse(response);


            //------------------------------------------------
            // Extract Fiona's Answer
            //------------------------------------------------

            const answer =
                String(response?.message || "").trim();


            if (!answer)
            {

                throw new Error(
                    "OmniScan returned an empty response."
                );

            }


            //------------------------------------------------
            // Display Fiona Response
            //------------------------------------------------

            setMessages(previous => [
                ...previous,
                {
                    id: Date.now() + Math.random(),
                    role: "fiona",
                    text: answer
                }
            ]);


            thinkingRef.current = false;
            setThinking(false);


            //------------------------------------------------
            // Speak Fiona Response
            //------------------------------------------------

            await speakResponse(
                answer,
                session
            );

        }
        catch (exception)
        {

            if (
                !activeRef.current ||
                session !== sessionRef.current
            )
            {

                return;

            }


            console.error(
                "Unable to ask Fiona:",
                exception
            );


            setMessages(previous => [
                ...previous,
                {
                    id: Date.now() + Math.random(),
                    role: "fiona",
                    text:
                        "Sorry, I couldn't respond just then."
                }
            ]);


            thinkingRef.current = false;
            setThinking(false);

            resumeListening();

        }

    };


    //====================================================
    // Finish User Speech
    //====================================================

    const finishUserSpeech = () =>
    {

        clearTimeout(silenceTimerRef.current);

        silenceTimerRef.current = null;


        const transcript =
            transcriptRef.current.trim();


        if (!transcript)
        {

            return;

        }


        transcriptRef.current = "";

        stopListening();


        sendFionaMessage(transcript).catch(
            exception =>
            {

                console.error(
                    "Unable to send voice message:",
                    exception
                );

            }
        );

    };


    finishSpeechRef.current = finishUserSpeech;


    //====================================================
    // Initialise Browser Speech Recognition
    //====================================================

    useEffect(
        () =>
        {

            mountedRef.current = true;


            const SpeechRecognition =
                window.SpeechRecognition ||
                window.webkitSpeechRecognition;


            if (!SpeechRecognition)
            {

                console.warn(
                    "Speech recognition is unavailable."
                );


                return () =>
                {

                    mountedRef.current = false;

                };

            }


            const recognition =
                new SpeechRecognition();


            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = "en-GB";


            //------------------------------------------------
            // Speech Result
            //------------------------------------------------

            recognition.onresult = event =>
            {

                if (
                    !activeRef.current ||
                    thinkingRef.current ||
                    speakingRef.current
                )
                {

                    return;

                }


                let finalTranscript = "";
                let interimTranscript = "";


                for (
                    let i = event.resultIndex;
                    i < event.results.length;
                    i++
                )
                {

                    const result =
                        event.results[i];


                    if (result.isFinal)
                    {

                        finalTranscript +=
                            result[0].transcript;

                    }
                    else
                    {

                        interimTranscript +=
                            result[0].transcript;

                    }

                }


                if (finalTranscript.trim())
                {

                    transcriptRef.current +=
                        (
                            transcriptRef.current
                                ? " "
                                : ""
                        ) +
                        finalTranscript.trim();

                }


                if (
                    transcriptRef.current.trim() ||
                    interimTranscript.trim()
                )
                {

                    clearTimeout(
                        silenceTimerRef.current
                    );


                    silenceTimerRef.current =
                        setTimeout(
                            () =>
                            {

                                finishSpeechRef.current?.();

                            },
                            SILENCE_DELAY
                        );

                }

            };


            //------------------------------------------------
            // Recognition Ended
            //------------------------------------------------

            recognition.onend = () =>
            {

                listeningRef.current = false;
                setListening(false);


                if (
                    activeRef.current &&
                    connectedRef.current &&
                    !thinkingRef.current &&
                    !speakingRef.current
                )
                {

                    resumeListening();

                }

            };


            //------------------------------------------------
            // Recognition Error
            //------------------------------------------------

            recognition.onerror = event =>
            {

                listeningRef.current = false;
                setListening(false);


                if (
                    event.error !== "no-speech" &&
                    event.error !== "aborted"
                )
                {

                    console.error(
                        "Fiona microphone error:",
                        event
                    );

                }

            };


            recognitionRef.current = recognition;


            //------------------------------------------------
            // Cleanup
            //------------------------------------------------

            return () =>
            {

                mountedRef.current = false;
                activeRef.current = false;

                ++sessionRef.current;

                clearTimers();

                recognition.onresult = null;
                recognition.onend = null;
                recognition.onerror = null;


                try
                {

                    recognition.abort();

                }
                catch (exception)
                {

                    // Already stopped.

                }


                recognitionRef.current = null;

            };

        },
        []
    );


    //====================================================
    // Start Live Expert
    //====================================================

    const startLiveExpert = () =>
    {

        if (activeRef.current)
        {

            return;

        }


        ++sessionRef.current;

        activeRef.current = true;
        connectedRef.current = true;
        thinkingRef.current = false;
        speakingRef.current = false;

        transcriptRef.current = "";

        clearTimers();


        setStarted(true);
        setConnecting(true);
        setConnected(true);
        setThinking(false);
        setSpeaking(false);
        setError(null);

        setMessages([]);

        //------------------------------------------------
        // Clear Previous Conversation Resources
        //------------------------------------------------

        setEvaResponse(null);


        //------------------------------------------------
        // EvaVideo Starts Simli When Mounted
        //
        // Its existing public API does not expose
        // a connected callback. Allow startup time
        // before enabling automatic listening.
        //------------------------------------------------

        const session = sessionRef.current;


        restartTimerRef.current = setTimeout(
            () =>
            {

                if (
                    activeRef.current &&
                    session === sessionRef.current
                )
                {

                    setConnecting(false);
                    startListening();

                }

            },
            3500
        );

    };


    //====================================================
    // Stop Live Expert
    //====================================================

    const stopLiveExpert = () =>
    {

        ++sessionRef.current;

        activeRef.current = false;
        connectedRef.current = false;
        thinkingRef.current = false;
        speakingRef.current = false;

        transcriptRef.current = "";

        clearTimers();
        stopListening();


        setStarted(false);
        setConnecting(false);
        setConnected(false);
        setThinking(false);
        setSpeaking(false);
        setError(null);

        //------------------------------------------------
        // Clear Resource Library
        //------------------------------------------------

        setEvaResponse(null);

    };


    //====================================================
    // Render
    //====================================================

    return (

        <div className="eva-live-expert">

            {/*========================================
               Fiona Video Area
            ========================================*/}

            <div className="eva-live-expert-video">

                {started && (

                    <EvaVideo
                        ref={evaVideoRef}
                        visible={started}
                    />

                )}


                {/*====================================
                   Start Screen
                =====================================*/}

                {!started && (

                    <div className="eva-live-expert-start">

                        <div className="eva-demo-handover-eyebrow">
                            EVA LIVE
                        </div>

                        <div className="eva-demo-handover-title">
                            Talk to an OmniScanPro Expert
                        </div>

                        <div className="eva-demo-handover-text">
                            Have a live conversation with
                            Fiona, your OmniScanPro
                            virtual expert.
                        </div>

                        <button
                            type="button"
                            className="eva-demo-primary-button"
                            onClick={startLiveExpert}
                        >
                            Start Conversation
                        </button>

                    </div>

                )}


                {/*====================================
                   Live Indicator
                =====================================*/}

                {started && (

                    <div className="eva-live-expert-connected">

                        <span
                            className="eva-demo-status-dot"
                        />

                        {connecting
                            ? "Connecting"
                            : listening
                                ? "Listening"
                                : thinking
                                    ? "Thinking"
                                    : speaking
                                        ? "Fiona speaking"
                                        : "Live"}

                    </div>

                )}


                {/*====================================
                   End Conversation
                =====================================*/}

                {started && (

                    <button
                        type="button"
                        className="eva-live-expert-end-button"
                        onClick={stopLiveExpert}
                    >
                        End Conversation
                    </button>

                )}


                {/*====================================
                   Error
                =====================================*/}

                {error && (

                    <div className="eva-live-expert-error">
                        {error}
                    </div>

                )}

            </div>


            {/*========================================
               Existing Purple Conversation Drawer
            ========================================*/}

            <EvaLiveConversationDrawer
                messages={messages}
                onSend={sendFionaMessage}
                connected={
                    connected &&
                    !thinking &&
                    !speaking
                }
            />


            {/*========================================
               Eva Resource Library
               
               Self-contained resource handling.
               Parent only passes the API response.
            ========================================*/}

            <div className="eva-live-expert-resource-library">

                <EvaResourceLibrary
                    response={evaResponse}
                />

            </div>

        </div>

    );

}
